import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Return } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useUpdateReturn } from '../hooks/use-return-mutations';
import { allowedFromConflict } from '../schemas/return-rules';

export interface ConfirmMoveDialogProps {
  storeId: string;
  ret: Return;
  move: 'rejected' | 'cancelled' | null;
  onOpenChange: (open: boolean) => void;
}

/** Rejecting and cancelling end the return; both ask first. */
export function ConfirmMoveDialog({ storeId, ret, move, onOpenChange }: ConfirmMoveDialogProps) {
  const { t } = useTranslation(['returns', 'common']);
  const message = useErrorMessage();
  const update = useUpdateReturn(
    storeId,
    move === 'rejected' ? 'returns:toasts.rejected' : 'returns:toasts.cancelled',
  );
  const allowed = allowedFromConflict(update.error);
  const key = move === 'rejected' ? 'reject' : 'cancel';

  return (
    <AlertDialog
      open={move !== null}
      onOpenChange={(next) => {
        if (!next) update.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(`returns:confirm.${key}.title`, { rma: ret.rmaNumber })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t(`returns:confirm.${key}.description`)}</AlertDialogDescription>
        </AlertDialogHeader>
        {update.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {allowed
                ? t('returns:errors.moveRefused', {
                    allowed: allowed.map((m) => t(`returns:status.${m}`)).join(', ') || '—',
                  })
                : message(update.error)}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={update.isPending}>
            {t('common:actions.back')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={update.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (move) {
                update.mutate(
                  { id: ret.id, body: { status: move } },
                  { onSuccess: () => onOpenChange(false) },
                );
              }
            }}
          >
            {t(`returns:confirm.${key}.submit`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
